/**
 * BlockInputForm — Daily SmartBlock input form.
 * Dynamically renders input fields from tracker type category definitions.
 * Supports number inputs and sliders based on input_type config.
 */
import React, { useState } from 'react';
import type { TrackerCategory, TrackerInput, BlockSubmitDiagnostics } from '@/lib/smartblocksApi';

type BlockInputFormProps = {
  categories: TrackerCategory[];
  baselines: Record<string, Record<string, number>>;
  onSubmit: (bitblocks: Record<string, Record<string, number>>) => Promise<void>;
  lastDiagnostics?: BlockSubmitDiagnostics | null;
  loading?: boolean;
};

export function BlockInputForm({
  categories,
  baselines,
  onSubmit,
  lastDiagnostics,
  loading = false,
}: BlockInputFormProps) {
  // Initialize values from baselines
  const initialValues: Record<string, Record<string, number>> = {};
  for (const cat of categories) {
    initialValues[cat.key] = {};
    for (const input of cat.inputs) {
      initialValues[cat.key][input.key] = baselines?.[cat.key]?.[input.key] ?? input.baseline;
    }
  }

  const [values, setValues] = useState(initialValues);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(
    categories[0]?.key ?? null,
  );

  const updateValue = (categoryKey: string, inputKey: string, value: number) => {
    setValues((prev) => ({
      ...prev,
      [categoryKey]: { ...prev[categoryKey], [inputKey]: value },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(values);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {categories.map((cat) => {
        const isExpanded = expandedCategory === cat.key;
        const catScore = lastDiagnostics?.category_scores?.[cat.key];

        return (
          <div
            key={cat.key}
            className="overflow-hidden rounded-xl border border-slate-200 bg-white"
          >
            {/* Category header — collapsible */}
            <button
              type="button"
              onClick={() => setExpandedCategory(isExpanded ? null : cat.key)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{cat.icon}</span>
                <span className="font-semibold text-cocoa">{cat.label}</span>
              </div>
              <div className="flex items-center gap-2">
                {catScore !== undefined && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      catScore >= 0.7
                        ? 'bg-emerald-100 text-emerald-700'
                        : catScore >= 0.45
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {Math.round(catScore * 100)}%
                  </span>
                )}
                <svg
                  className={`h-4 w-4 text-cocoa/40 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {/* Category inputs */}
            {isExpanded && (
              <div className="border-t border-slate-100 px-4 py-3 space-y-4">
                {cat.inputs.map((input) => (
                  <InputField
                    key={input.key}
                    input={input}
                    value={values[cat.key]?.[input.key] ?? input.baseline}
                    baseline={baselines?.[cat.key]?.[input.key] ?? input.baseline}
                    onChange={(v) => updateValue(cat.key, input.key, v)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
      >
        {loading ? 'Submitting...' : 'Seal & Submit Block'}
      </button>
    </form>
  );
}

type InputFieldProps = {
  input: TrackerInput;
  value: number;
  baseline: number;
  onChange: (value: number) => void;
};

function InputField({ input, value, baseline, onChange }: InputFieldProps) {
  const ratio = baseline > 0 ? value / baseline : 0;
  const indicator =
    ratio >= 1.5
      ? { color: 'text-emerald-600', label: 'Excellent' }
      : ratio >= 1.0
        ? { color: 'text-emerald-500', label: 'On target' }
        : ratio >= 0.7
          ? { color: 'text-amber-500', label: 'Below target' }
          : { color: 'text-red-500', label: 'Needs attention' };

  if (input.input_type === 'slider') {
    return (
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-sm font-medium text-cocoa">{input.label}</label>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${indicator.color}`}>{indicator.label}</span>
            <span className="text-sm font-semibold text-cocoa">
              {value}
              {input.unit ? ` ${input.unit}` : ''}
            </span>
          </div>
        </div>
        <input
          type="range"
          min={input.min}
          max={input.max}
          step={input.step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full accent-emerald-600"
        />
        <div className="flex justify-between text-xs text-cocoa/40">
          <span>{input.min}</span>
          <span className="text-cocoa/50">baseline: {baseline}</span>
          <span>{input.max}</span>
        </div>
      </div>
    );
  }

  // Number input
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-sm font-medium text-cocoa">{input.label}</label>
        <span className={`text-xs ${indicator.color}`}>{indicator.label}</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={input.min}
          max={input.max}
          step={input.step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-cocoa focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
        />
        <span className="whitespace-nowrap text-xs text-cocoa/50">{input.unit}</span>
      </div>
      <p className="mt-0.5 text-xs text-cocoa/40">baseline: {baseline} {input.unit}</p>
    </div>
  );
}

export default BlockInputForm;
